import type { ReactNode } from 'react';
import { Button } from './Button';

const isActiveValue = (value: unknown) => {
  if (Array.isArray(value)) return value.length > 0;
  return value != null && value !== '';
};

export interface FilterGroupProps {
  // Current value of each filter/search control placed inside this group
  // (e.g. [search, status, severity]) — used only to detect whether any
  // filter is active, so the Clear button can hide/disable itself when
  // there's nothing to clear. FilterGroup does not read or write these
  // values directly; the parent still owns all filter state.
  filters: unknown[];
  // Resets every filter back to its default value. The parent implements
  // this (e.g. setSearch(''); setStatus(''); setSeverity([]);).
  onClear: () => void;
  clearLabel?: string;
  className?: string;
  children?: ReactNode;
}

// Lays out a row of filter controls (SearchInput, FilterDropdown,
// CustomSelect, ...) alongside a "Clear filters" button that resets all of
// them at once. FilterGroup stays a thin layout + button — it has no
// knowledge of any individual filter's internals, so any control can be
// placed inside as long as the parent includes its value in `filters` and
// resets it in `onClear`.
export const FilterGroup = ({ filters, onClear, clearLabel = 'Clear filters', className = '', children }: FilterGroupProps) => {
  const hasActiveFilter = filters.some(isActiveValue);

  return (
    <div className={`flex items-center gap-3 flex-wrap ${className}`}>
      {children}
      {hasActiveFilter && (
        <Button
          variant="tertiary"
          onClick={onClear}
        >
          {clearLabel}
        </Button>
      )}
    </div>
  );
};
